import React, { useState } from 'react';
import { connect } from 'react-redux';

import Form from 'react-bootstrap/Form';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';

import emailjs from 'emailjs-com';

import ConfirmProjectMobile from './ConfirmProjectMobile';

import { createProject } from '../../../../store/actions/projectActions';
import { updateUsersNotifications } from '../../../../store/actions/userActions';

/* ---------------- FACTORIES ---------------- */

const makeBlankContacts = () => ({
  primary: { nameTitle: '', phone: '' },
  alternate: { nameTitle: '', phone: '' },
  onsite: { nameTitle: '', phone: '' },
});

const makeBlankProject = () => ({
  client: '',
  alias: '',
  address: '',
  city: '',
  addressLink: '',
  workstations: '',
  type: '',
  day: '',
  startTime: '',
  endTime: '',
  bartStation: '',
  bartDistance: '',
  notes: '',
  contacts: makeBlankContacts(),
});

/* ---------------- STABLE CONTACT ROW (IMPORTANT FOR MOBILE FOCUS) ---------------- */

const ContactRow = ({ index, label, typeKey, contact, onContactChange }) => {
  const safe = contact || { nameTitle: '', phone: '' };

  return (
    <Form.Row style={{ marginBottom: 6 }}>
      <Form.Group as={Col} xs={12} controlId={`${typeKey}NameTitle-${index}`}>
        <Form.Label style={{ marginBottom: 4 }}>{label} (Name/Title)</Form.Label>
        <Form.Control
          id={`${typeKey}-nameTitle-${index}`}
          type="text"
          data-index={index}
          data-contact-type={typeKey}
          data-field="nameTitle"
          placeholder="Jane Doe / Facilities"
          value={safe.nameTitle}
          onChange={onContactChange}
        />
      </Form.Group>

      <Form.Group as={Col} xs={12} controlId={`${typeKey}Phone-${index}`}>
        <Form.Label style={{ marginBottom: 4 }}>{label} Phone</Form.Label>
        <Form.Control
          id={`${typeKey}-phone-${index}`}
          type="tel"
          inputMode="tel"
          data-index={index}
          data-contact-type={typeKey}
          data-field="phone"
          placeholder="(555) 555-5555"
          value={safe.phone}
          onChange={onContactChange}
        />
      </Form.Group>
    </Form.Row>
  );
};

/* ---------------- COMPONENT ---------------- */

const CreateProjectMobile = (props) => {
  const [projectState, setProjectState] = useState([makeBlankProject()]);

  // per-project dropdown: none | primary | alternate | onsite | all
  const [contactsMode, setContactsMode] = useState(['none']);

  // IMPORTANT: used to force-remount the form to clear uncontrolled fields
  const [formKey, setFormKey] = useState(0);

  /* ---------------- ADD PROJECT ---------------- */

  const addProject = () => {
    setProjectState((prev) => {
      const first = prev[0];

      return [
        ...prev,
        {
          client: first.client,
          alias: first.alias,
          address: first.address,
          city: first.city,
          addressLink: first.addressLink,
          workstations: first.workstations,
          type: '',
          day: '',
          startTime: '',
          endTime: '',
          bartStation: first.bartStation,
          bartDistance: first.bartDistance,
          notes: first.notes,
          // deep copy contacts so each project has its own object
          contacts: {
            primary: { ...first.contacts.primary },
            alternate: { ...first.contacts.alternate },
            onsite: { ...first.contacts.onsite },
          },
        },
      ];
    });

    setContactsMode((prev) => [...prev, prev[0] || 'none']);
  };

  /* ---------------- PROJECT FIELDS ---------------- */

  const handleProjectChange = (e) => {
    const idx = Number(e.target.dataset.index);
    const field = e.target.id;
    const value = e.target.value;

    setProjectState((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p))
    );
  };

  /* ---------------- CONTACT FIELDS ---------------- */

  const handleContactChange = (e) => {
    const idx = Number(e.target.dataset.index);
    const contactType = e.target.dataset.contactType;
    const field = e.target.dataset.field;
    const value = e.target.value;

    setProjectState((prev) =>
      prev.map((p, i) => {
        if (i !== idx) return p;

        return {
          ...p,
          contacts: {
            ...p.contacts,
            [contactType]: {
              ...p.contacts[contactType],
              [field]: value,
            },
          },
        };
      })
    );
  };

  /* ---------------- CONTACT MODE ---------------- */

  const handleContactsModeChange = (e) => {
    const idx = Number(e.target.dataset.index);
    const value = e.target.value;

    setContactsMode((prev) => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  };

  const shouldShow = (mode, key) => {
    if (!mode || mode === 'none') return false;
    if (mode === 'all') return true;
    return mode === key;
  };

  /* ---------------- RESET ---------------- */

  const handleFormReset = () => {
    setProjectState([makeBlankProject()]);
    setContactsMode(['none']);
    setFormKey((k) => k + 1); // clears uncontrolled inputs by remounting
  };

  /* ---------------- SUBMIT ---------------- */

  const handleSubmit = (event) => {
    event.preventDefault();

    const payload = projectState.map((project) => {
      const next = { ...project };

      next.startTime = next.day + ' ' + next.startTime;
      next.endTime = next.day + ' ' + next.endTime;
      delete next.day;

      if (next.type === 'Post-move') {
        next.workstations = 0;
      }

      return next;
    });

    props.createProject(payload);
    handleFormReset();
  };

  /* ---------------- EMAIL ---------------- */

  const sendEmail = () => {
    const allEmails = [];

    Object.entries(props.users || {}).forEach(([id, user]) => {
      if (user.role === 'tech' || user.role === 'lead' || user.role === 'supervisor' && user.notifications === undefined) {
        props.updateUsersNotifications(id);
        if (user.email) allEmails.push(user.email);
      } else if (user.role === 'tech' || user.role === 'lead' || user.role === 'supervisor' && user.email) {
        if (
          user.notifications &&
          user.notifications.email &&
          user.notifications.email.newProjects
        ) {
          allEmails.push(user.email);
        }
      }
    });

    while (allEmails.length) {
      const templateParams = { Bcc: allEmails.splice(0, 98) };
      emailjs.send(
        'scheduler@murphymckay',
        'template_myp5vme',
        templateParams,
        'user_UgmdOeeH6OCjwrAqPoGpi'
      );
      console.log("Sent to", templateParams.Bcc)
    }
  };

  /* ---------------- RENDER ---------------- */

  return (
    <div className="dashboard container">
      <Form key={formKey} onSubmit={handleSubmit}>
        <h4>Create a New Project</h4>

        {projectState.map((val, index) => {
          const mode = contactsMode[index] || 'none';

          const contactsForIndex =
            projectState[index] && projectState[index].contacts
              ? projectState[index].contacts
              : makeBlankContacts();

          return (
            <div key={index}>
              <Form.Row>
                <Form.Group as={Col} controlId="type">
                  <Form.Label>Type</Form.Label>
                  <Form.Control
                    as="select"
                    data-index={index}
                    onChange={handleProjectChange}
                    required
                  >
                    <option></option>
                    <option>Disconnect</option>
                    <option>Reconnect</option>
                    <option>Shuffle</option>
                    <option>Post-move</option>
                    <option>Server</option>
                    <option>Support</option>
                  </Form.Control>
                </Form.Group>

                <Form.Group as={Col} controlId="workstations">
                  <Form.Label>Workstations</Form.Label>
                  <Form.Control
                    type="number"
                    data-index={index}
                    placeholder="Enter a number"
                    onChange={handleProjectChange}
                    required
                  />
                </Form.Group>
              </Form.Row>

              <Form.Row>
                <Form.Group as={Col} controlId="client">
                  <Form.Label>Client</Form.Label>
                  <Form.Control
                    type="text"
                    data-index={index}
                    onChange={handleProjectChange}
                    required
                  />
                </Form.Group>

                <Form.Group as={Col} controlId="alias">
                  <Form.Label>Client ID</Form.Label>
                  <Form.Control
                    type="text"
                    data-index={index}
                    onChange={handleProjectChange}
                    required
                  />
                </Form.Group>
              </Form.Row>

              <Form.Row>
                <Form.Group as={Col} controlId="address">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    type="text"
                    data-index={index}
                    onChange={handleProjectChange}
                    required
                  />
                </Form.Group>

                <Form.Group as={Col} controlId="city">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    data-index={index}
                    onChange={handleProjectChange}
                    required
                  />
                </Form.Group>

                <Form.Group as={Col} controlId="addressLink">
                  <Form.Label>Google Maps Link</Form.Label>
                  <Form.Control
                    type="text"
                    data-index={index}
                    onChange={handleProjectChange}
                  />
                </Form.Group>
              </Form.Row>

              <Form.Row>
                <Form.Group as={Col} controlId="day">
                  <Form.Label>Date</Form.Label>
                  <Form.Control
                    type="date"
                    data-index={index}
                    onChange={handleProjectChange}
                    required
                  />
                </Form.Group>

                <Form.Group as={Col} controlId="startTime">
                  <Form.Label>Start Time</Form.Label>
                  <Form.Control
                    type="time"
                    data-index={index}
                    onChange={handleProjectChange}
                    required
                  />
                </Form.Group>

                <Form.Group as={Col} controlId="endTime">
                  <Form.Label>End Time</Form.Label>
                  <Form.Control
                    type="time"
                    data-index={index}
                    onChange={handleProjectChange}
                    required
                  />
                </Form.Group>
              </Form.Row>

              <Form.Row>
                <Form.Group as={Col} controlId="bartStation">
                  <Form.Label>Nearest BART Station</Form.Label>
                  <Form.Control
                    type="text"
                    data-index={index}
                    onChange={handleProjectChange}
                  />
                </Form.Group>

                <Form.Group as={Col} controlId="bartDistance">
                  <Form.Label>Distance to BART</Form.Label>
                  <Form.Control
                    type="number"
                    data-index={index}
                    onChange={handleProjectChange}
                  />
                </Form.Group>
              </Form.Row>

              <Form.Row>
                <Form.Group as={Col} controlId="notes">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    data-index={index}
                    onChange={handleProjectChange}
                  />
                </Form.Group>
              </Form.Row>

              {/* CONTACTS */}
              <div style={{ marginTop: 10 }}>
                <Form.Row>
                  <Form.Group as={Col}>
                    <Form.Label>Contacts</Form.Label>

                    <Form.Control
                      as="select"
                      data-index={index}
                      value={mode}
                      onChange={handleContactsModeChange}
                      size="sm"
                    >
                      <option value="none">None</option>
                      <option value="primary">Primary</option>
                      <option value="alternate">Alternate</option>
                      <option value="onsite">Onsite</option>
                      <option value="all">All</option>
                    </Form.Control>
                  </Form.Group>
                </Form.Row>

                {shouldShow(mode, 'primary') && (
                  <ContactRow
                    index={index}
                    label="Primary"
                    typeKey="primary"
                    contact={contactsForIndex.primary}
                    onContactChange={handleContactChange}
                  />
                )}

                {shouldShow(mode, 'alternate') && (
                  <ContactRow
                    index={index}
                    label="Alternate"
                    typeKey="alternate"
                    contact={contactsForIndex.alternate}
                    onContactChange={handleContactChange}
                  />
                )}

                {shouldShow(mode, 'onsite') && (
                  <ContactRow
                    index={index}
                    label="Onsite"
                    typeKey="onsite"
                    contact={contactsForIndex.onsite}
                    onContactChange={handleContactChange}
                  />
                )}
              </div>
            </div>
          );
        })}

        <Button variant="primary" type="button" onClick={addProject}>
          Add Project
        </Button>{' '}
        <Button variant="success" type="submit">
          Submit
        </Button>
      </Form>

      <ConfirmProjectMobile />

      <Button variant="primary" type="button" onClick={sendEmail}>
        Send Emails
      </Button>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  createProject: (projectArray) => dispatch(createProject(projectArray)),
  updateUsersNotifications: (id) => dispatch(updateUsersNotifications(id)),
});

export default connect(null, mapDispatchToProps)(CreateProjectMobile);
